/**
 * useAutosave: debounced save to Supabase whenever the editor's object
 * graph changes (PRD §11.1). Writes through the same useSaveLayout
 * mutation the explicit "Save" toolbar button calls — one write path,
 * not two, so autosave and manual save can never drift out of sync.
 *
 * Debounce window: 1.5s of inactivity after the last change. PRD's
 * suggested "every 30 seconds or on significant action" is intentionally
 * tightened here — 30s risked losing meaningful work on a closed tab,
 * and reacting to every individual drag-end (a "significant action" in
 * the PRD's words) already debounced through this hook is both safer and
 * not meaningfully more expensive than waiting half a minute.
 */

import { useEffect, useRef } from 'react';
import { useEditorStore } from '../store/useEditorStore';
import { useSaveLayout } from '../api/layouts';

const DEBOUNCE_MS = 1500;

export function useAutosave(weddingId: string | undefined) {
  const objects = useEditorStore((state) => state.objects);
  const showWarningsOnSetupSheet = useEditorStore((state) => state.showWarningsOnSetupSheet);
  const saveLayout = useSaveLayout();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Seeded lazily from the FIRST objects value this hook ever sees for a
  // given wedding, not an empty string — otherwise the very first render
  // after loading an existing layout looks like a "change" and triggers
  // one pointless save before any real edit has happened.
  const lastSavedRef = useRef<string | null>(null);
  const lastWeddingIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!weddingId) return;

    const serialized = JSON.stringify({ objects, showWarningsOnSetupSheet });

    // Wedding changed (navigated to a different layout) — reset the
    // baseline to whatever this new layout's initial objects are, rather
    // than comparing against the previous wedding's last-saved state.
    if (lastWeddingIdRef.current !== weddingId) {
      lastWeddingIdRef.current = weddingId;
      lastSavedRef.current = serialized;
      return;
    }

    if (serialized === lastSavedRef.current) {
      // Nothing actually changed since the last save — skip the network call.
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      saveLayout.mutate(
        { weddingId, objects, showWarningsOnSetupSheet },
        {
          onSuccess: () => {
            lastSavedRef.current = serialized;
          },
        }
      );
    }, DEBOUNCE_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveLayout is a stable mutation object; including it would re-trigger the debounce timer unnecessarily
  }, [objects, weddingId, showWarningsOnSetupSheet]);

  return {
    isSaving: saveLayout.isPending,
    lastError: saveLayout.error,
  };
}
